import React, { useEffect, useState, useCallback, useRef } from "react";
import { Line } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Alert,
  useTheme
} from "@mui/material";
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { baseURL } from "../../utils/StaticVariables";
import OrderFilter from "./OrderFilter";
import DesBtn from "../Reusable/DesBtn";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
  ChartDataLabels
);

export default function AveragePersonsByFloorAndHour() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});
  const theme = useTheme(); // 🌙 للحصول على Dark / Light mode

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `analytics/floors/people-by-hour`;
      const response = await axios.get(`${baseURL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const responseData = response.data?.data || [];

      if (!Array.isArray(responseData) || responseData.length === 0) {
        setChartData({
          labels: [...Array(24).keys()],
          datasets: [],
        });
        setLoading(false);
        return;
      }

      const floors = [...new Set(responseData.map((i) => i.floor_level))].sort((a,b)=>a-b);
      const hours = [...Array(24).keys()];

      const datasets = floors.map((floor, index) => {
        const values = hours.map((h) => {
          const filtered = responseData.filter(
            (item) => item.floor_level === floor && Number(item.hour) === h
          );
          if (!filtered.length) return 0;
          return Number((filtered.reduce((acc, r) => acc + Number(r.avg_person_count || 0), 0) / filtered.length).toFixed(2));
        });

        const hue = (index * 137.5) % 360;

        return {
          label: `Floor ${floor}`,
          data: values,
          borderColor: `hsla(${hue}, 70%, 50%, 1)`,
          backgroundColor: `hsla(${hue}, 70%, 60%, 0.2)`,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: `hsla(${hue}, 70%, 50%, 1)`,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        };
      });

      setChartData({ labels: hours, datasets });

    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError(err.response?.data?.message || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [token]);

    useEffect(() => {
      fetchData();
    }, [filter]);
  

  if (loading) return (
    <Card sx={{ p: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </CardContent>
    </Card>
  );

  if (error) return (
    <Card sx={{ p: 2 }}>
      <CardContent>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </CardContent>
    </Card>
  );

  if (!chartData || chartData.datasets.length === 0) return (
    <Card sx={{ p: 2 }}>
      <CardContent>
        <Alert severity="info">No data available for the selected period</Alert>
      </CardContent>
    </Card>
  );
  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };
  return (
    <Card sx={{ p: 2 }}>

       <DesBtn text="Filter" handle={openFilter}>
              <FilterAltOutlinedIcon />
            </DesBtn>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Average Number of Persons by Floor Level and Hour
        </Typography>

        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { 
                position: "top",
                align: "end",
                labels: { boxWidth: 20, padding: 15, usePointStyle: true, color: theme.palette.text.primary }
              },
              title: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} persons`
                }
              },
              datalabels: {
                color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                font: { weight: 'bold' },
                anchor: 'end',
                align: 'top',
                formatter: (value) => value.toFixed(2)
              }
            },
            scales: {
              x: {
                title: { display: true, text: "Hour of Day (0–23)", font: { weight: 'bold' }, color: theme.palette.text.primary },
                grid: { display: true, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
                ticks: { color: theme.palette.text.primary }
              },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Average Person Count", font: { weight: 'bold' }, color: theme.palette.text.primary },
                ticks: { precision: 0, color: theme.palette.text.primary },
                grid: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }
              }
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
